export const CAPABILITIES = [
    "network",
    "clipboard.read",
    "clipboard.write",
    "files.read",
    "files.write",
    "photos.read",
    "photos.write",
    "health.read",
    "health.write",
    "calendar.read",
    "calendar.write",
    "contacts.read",
    "location.current",
    "notifications.local",
    "share.receive",
    "webpage.inspect",
];
export const SHORTCUT_INPUT_TYPES = ["text", "url", "image", "file"];
export class ManifestValidationError extends Error {
    issues;
    constructor(issues) {
        super(issues.map((issue) => `${issue.code} ${issue.path}: ${issue.message}`).join("\n"));
        this.name = "ManifestValidationError";
        this.issues = issues;
    }
}
const CAPABILITY_SET = new Set(CAPABILITIES);
const INPUT_TYPE_SET = new Set(SHORTCUT_INPUT_TYPES);
const APP_ID = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?){2,}$/;
const SEMVER = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/;
const APPLE_VERSION = /^(0|[1-9]\d*)\.(0|[1-9]\d*)(?:\.(0|[1-9]\d*))?$/;
const PROVIDER_ID = /^[a-z][a-z0-9-]*$/;
const PARTIAL_SEMVER = /^(0|[1-9]\d*)(?:\.(0|[1-9]\d*))?(?:\.(0|[1-9]\d*))?(?:-[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/;
export function isCapability(value) {
    return typeof value === "string" && CAPABILITY_SET.has(value);
}
export function isSemanticVersion(value) {
    return typeof value === "string" && SEMVER.test(value);
}
export function isAppleVersion(value) {
    return typeof value === "string" && APPLE_VERSION.test(value);
}
export function normalizeAllowedOrigin(value) {
    let url;
    try {
        url = new URL(value);
    }
    catch {
        return null;
    }
    const localhost = url.hostname === "localhost" || url.hostname === "127.0.0.1" || url.hostname === "[::1]";
    if ((url.protocol !== "https:" && !(url.protocol === "http:" && localhost)) || url.username || url.password || url.hostname.includes("*")) {
        return null;
    }
    if (url.pathname !== "/" || url.search || url.hash || url.origin === "null") {
        return null;
    }
    return url.origin;
}
export function normalizePublicUrl(value) {
    let url;
    try {
        url = new URL(value);
    }
    catch {
        return null;
    }
    if (url.protocol !== "https:" || url.username || url.password || url.search || url.hash) {
        return null;
    }
    return url.href;
}
export function validateCapabilities(values) {
    if (!Array.isArray(values)) {
        return [{ code: "TCH-CAP-001", path: "capabilities", message: "Expected a capability array." }];
    }
    const issues = [];
    const seen = new Set();
    values.forEach((value, index) => {
        if (!isCapability(value)) {
            issues.push({ code: "TCH-CAP-001", path: `capabilities[${index}]`, message: `Unknown capability: ${String(value)}` });
            return;
        }
        if (seen.has(value)) {
            issues.push({ code: "TCH-CAP-002", path: `capabilities[${index}]`, message: `Duplicate capability: ${value}` });
        }
        seen.add(value);
    });
    return issues;
}
export function validateAppManifest(input) {
    if (!isRecord(input)) {
        return { ok: false, issues: [{ code: "TCH-MANIFEST-001", path: "$", message: "Manifest must be an object." }] };
    }
    const issues = [];
    const add = (code, path, message) => {
        issues.push({ code, path, message });
    };
    if (input.schemaVersion !== undefined && input.schemaVersion !== 1) {
        add("TCH-MANIFEST-002", "schemaVersion", "Only schema version 1 is supported.");
    }
    if (typeof input.id !== "string" || !APP_ID.test(input.id)) {
        add("TCH-MANIFEST-003", "id", "Use a lowercase reverse-domain ID such as dev.tchoma.wishlist.");
    }
    if (!isNonEmptyString(input.name))
        add("TCH-MANIFEST-005", "name", "Name is required.");
    if (!isSemanticVersion(input.version))
        add("TCH-MANIFEST-004", "version", "Version must be strict semantic versioning.");
    if (!isNonEmptyString(input.description))
        add("TCH-MANIFEST-005", "description", "Description is required.");
    validateAssets(input, add);
    validateSurfaces(input, add);
    issues.push(...validateCapabilities(input.capabilities));
    const capabilities = Array.isArray(input.capabilities) ? input.capabilities.filter(isCapability) : [];
    const origins = validateNetwork(input.network, add);
    if (origins.length > 0 && !capabilities.includes("network")) {
        add("TCH-CAP-004", "network.allowedOrigins", "Allowed origins require the network capability.");
    }
    if (isRecord(input.surfaces)
        && isRecord(input.surfaces.shortcut)
        && input.surfaces.shortcut.showInShareSheet === true
        && !capabilities.includes("share.receive")) {
        add("TCH-CAP-004", "surfaces.shortcut.showInShareSheet", "Share Sheet shortcuts require the share.receive capability.");
    }
    validateData(input.data, add);
    validateCompatibility(input, add);
    if (issues.length > 0)
        return { ok: false, issues };
    const source = input;
    const shortcutName = source.surfaces.shortcut.name?.trim() || `${source.name.trim()} · tchoma`;
    const value = {
        schemaVersion: 1,
        id: source.id,
        name: source.name.trim(),
        version: source.version,
        description: source.description.trim(),
        icon: { ...source.icon },
        screenshots: [...source.screenshots],
        surfaces: {
            web: {
                ...source.surfaces.web,
                ...(source.surfaces.web.publicUrl
                    ? { publicUrl: normalizePublicUrl(source.surfaces.web.publicUrl) }
                    : {}),
            },
            shortcut: {
                ...source.surfaces.shortcut,
                name: shortcutName,
                accepts: [...source.surfaces.shortcut.accepts],
            },
        },
        capabilities: [...source.capabilities].sort(),
        network: { allowedOrigins: origins.sort() },
        data: {
            ...source.data,
            sync: { ...source.data.sync },
        },
        minimum: { ...source.minimum },
        bridgeProtocol: source.bridgeProtocol,
        migrationVersion: source.migrationVersion,
        shortcutShell: source.shortcutShell.trim(),
        updateCompatibility: source.updateCompatibility.trim(),
    };
    return { ok: true, value };
}
export function parseAppManifest(input) {
    const result = validateAppManifest(input);
    if (!result.ok)
        throw new ManifestValidationError(result.issues);
    return result.value;
}
export function serializeAppManifest(input) {
    return stableJson(parseAppManifest(input));
}
function validateAssets(input, add) {
    if (!isRecord(input.icon)) {
        add("TCH-MANIFEST-005", "icon", "Icon configuration is required.");
    }
    else {
        for (const key of ["source", "shortcutGlyph", "shortcutColor"]) {
            if (!isNonEmptyString(input.icon[key]))
                add("TCH-MANIFEST-005", `icon.${key}`, `${key} is required.`);
        }
    }
    if (!Array.isArray(input.screenshots) || input.screenshots.some((value) => !isNonEmptyString(value))) {
        add("TCH-MANIFEST-005", "screenshots", "Screenshots must be an array of asset paths.");
    }
}
function validateSurfaces(input, add) {
    if (!isRecord(input.surfaces) || !isRecord(input.surfaces.web) || !isRecord(input.surfaces.shortcut)) {
        add("TCH-MANIFEST-006", "surfaces", "Web and Shortcut surfaces are required.");
        return;
    }
    const web = input.surfaces.web;
    if (!isNonEmptyString(web.entry) || !["standalone", "minimal-ui", "browser"].includes(String(web.display)) || typeof web.offline !== "boolean") {
        add("TCH-MANIFEST-006", "surfaces.web", "Web surface requires entry, display and offline fields.");
    }
    if (web.publicUrl !== undefined && (typeof web.publicUrl !== "string" || normalizePublicUrl(web.publicUrl) === null)) {
        add("TCH-MANIFEST-006", "surfaces.web.publicUrl", "Public web URL must be HTTPS without credentials, query or fragment.");
    }
    const shortcut = input.surfaces.shortcut;
    const accepts = Array.isArray(shortcut.accepts) && shortcut.accepts.every((value) => typeof value === "string" && INPUT_TYPE_SET.has(value));
    if (!isNonEmptyString(shortcut.entry) || !accepts || typeof shortcut.showInShareSheet !== "boolean" || typeof shortcut.showOnAppleWatch !== "boolean") {
        add("TCH-MANIFEST-006", "surfaces.shortcut", "Shortcut surface requires entry, accepted input types and launch flags.");
    }
    if (shortcut.name !== undefined && !isNonEmptyString(shortcut.name)) {
        add("TCH-MANIFEST-006", "surfaces.shortcut.name", "Shortcut name cannot be empty.");
    }
}
function validateNetwork(network, add) {
    if (!isRecord(network) || !Array.isArray(network.allowedOrigins)) {
        add("TCH-NET-001", "network.allowedOrigins", "Expected an origin allowlist.");
        return [];
    }
    const normalized = [];
    const seen = new Set();
    network.allowedOrigins.forEach((origin, index) => {
        const parsed = typeof origin === "string" ? normalizeAllowedOrigin(origin) : null;
        if (!parsed) {
            add("TCH-NET-001", `network.allowedOrigins[${index}]`, "Use an HTTPS origin without path, credentials, query or fragment.");
            return;
        }
        if (seen.has(parsed)) {
            add("TCH-NET-001", `network.allowedOrigins[${index}]`, `Duplicate origin: ${parsed}`);
            return;
        }
        seen.add(parsed);
        normalized.push(parsed);
    });
    return normalized;
}
function validateData(data, add) {
    if (!isRecord(data) || data.local !== "indexeddb" || !["opfs-with-indexeddb-fallback", "indexeddb"].includes(String(data.blobs)) || !isRecord(data.sync)) {
        add("TCH-DATA-001", "data", "Data config requires IndexedDB local storage, blob storage and a sync provider.");
        return;
    }
    const sync = data.sync;
    if (typeof sync.provider !== "string" || !PROVIDER_ID.test(sync.provider) || typeof sync.documents !== "boolean" || typeof sync.blobs !== "boolean" || typeof sync.realtime !== "boolean") {
        add("TCH-DATA-001", "data.sync", "Sync provider and feature flags are invalid.");
    }
    if (sync.provider === "none" && (sync.documents || sync.blobs || sync.realtime)) {
        add("TCH-DATA-001", "data.sync", "The none provider cannot enable cloud features.");
    }
}
function validateCompatibility(input, add) {
    if (!isRecord(input.minimum) || !isAppleVersion(input.minimum.ios) || !isAppleVersion(input.minimum.macos)) {
        add("TCH-OS-001", "minimum", "Minimum iOS and macOS versions must look like 26.0 or 26.0.1.");
    }
    else if (input.minimum.shortcutClient !== undefined && (!Number.isInteger(input.minimum.shortcutClient) || Number(input.minimum.shortcutClient) < 1)) {
        add("TCH-OS-001", "minimum.shortcutClient", "Shortcut client version must be a positive integer.");
    }
    if (!Number.isInteger(input.bridgeProtocol) || Number(input.bridgeProtocol) < 1) {
        add("TCH-COMPAT-001", "bridgeProtocol", "Bridge protocol must be a positive integer.");
    }
    if (!Number.isInteger(input.migrationVersion) || Number(input.migrationVersion) < 0) {
        add("TCH-COMPAT-001", "migrationVersion", "Migration version must be a non-negative integer.");
    }
    if (!isCompatibilityRange(input.shortcutShell)) {
        add("TCH-COMPAT-001", "shortcutShell", "Shortcut shell compatibility range is invalid.");
    }
    if (!isCompatibilityRange(input.updateCompatibility)) {
        add("TCH-COMPAT-001", "updateCompatibility", "Update compatibility range is invalid.");
    }
}
export function isCompatibilityRange(value) {
    if (typeof value !== "string" || value.trim().length === 0)
        return false;
    return value.split("||").every((alternative) => {
        const tokens = alternative.trim().split(/\s+/);
        return tokens.length > 0 && tokens.every((token) => {
            if (token === "*")
                return true;
            const match = /^(?:<=|>=|<|>|=|\^|~)?(.+)$/.exec(token);
            return match !== null && PARTIAL_SEMVER.test(match[1]);
        });
    });
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isNonEmptyString(value) {
    return typeof value === "string" && value.trim().length > 0;
}
function stableJson(value) {
    return JSON.stringify(sortKeys(value), null, 2) + "\n";
}
function sortKeys(value) {
    if (Array.isArray(value))
        return value.map(sortKeys);
    if (!isRecord(value))
        return value;
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, sortKeys(value[key])]));
}
//# sourceMappingURL=index.js.map